/** TODO: determine if this is still in use anywhere. (It may have been added here just for
 * form field wrapper, in which case we don't use this anymore) */

import React, { Children, Component } from 'react'
import PropTypes from 'prop-types'
import { Icon } from 'semantic-ui-react'
import cx from 'classnames'
import cls from './styles.scss'

export const TIME_IT_TAKES_TO_MOVE_MOUSE_FROM_ANCHOR_TO_TOOLTIP = 200 // ms

class TooltipAnchor extends Component {
  separateTooltipFromChildren() {
    const childTooltip = []
    const otherChildren = []

    Children.forEach(this.props.children, (child, idx) => {
      if (child.type === Tooltip) {
        child = React.cloneElement(child, {
          elmId: child.props.elmId || this.props.elmId,
          key: idx,
        })
        childTooltip.push(child)
      } else {
        otherChildren.push(child)
      }
    })

    return {
      children: otherChildren,
      tooltip: childTooltip,
    }
  }

  render() {
    const separatedChildren = this.separateTooltipFromChildren()

    return (
      <div className={cx(cls.root, this.props.className)}>
        <div
          className={cx(cls['anchor-content'])}
          data-elm-id={`${this.props.elmId}_trigger`}
        >
          {separatedChildren.children}
        </div>
        {separatedChildren.tooltip}
      </div>
    )
  }
}

TooltipAnchor.propTypes = {
  children: function (props, propName, componentName) {
    let numberOfTooltips = 0
    Children.forEach(props.children, child => {
      if (child.type === Tooltip) {
        numberOfTooltips++
      }
    })

    if (numberOfTooltips !== 1) {
      throw new Error(
        'TooltipAnchor should be passed exactly one Tooltip component as an immediate child, ' +
          numberOfTooltips +
          ' found'
      )
    }
  },
  className: PropTypes.string,
  elmId: PropTypes.string,
}

class Tooltip extends Component {
  static propTypes = {
    position: PropTypes.oneOf(['left', 'right', 'top', 'bottom']).isRequired,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func,
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node),
    ]),
    className: PropTypes.string,
    elmId: PropTypes.string,
    error: PropTypes.bool,
    autoCloseOnMouseLeave: PropTypes.bool,
    alwaysCloseOnClickOutside: PropTypes.bool,
  }

  state = {
    holdOpen: false,
    isMouseOver: false,
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    /*
      This logic allows for a "hovering tooltip" where the tooltip is opened by hovering
      over the anchor initially - and is kept open by hovering over the tooltip itself.

      Here, if tooltip is closing and autoCloseOnMouseLeave is true, we hold the tooltip
      open for a brief period of time to allow the user to move the mouse from the
      anchor over to the toolip. When the time is up, if the mouse is not over
      the tooltip, we close it (i.e. the user just moused over the anchor and moved
      away without hovering over the tooltip.)

      If the user IS hovering over the tooltip, we hold it open indefinitely, until
      the user mouses away from the tooltip itself (handled in mouseleave event).
     */
    if (
      nextProps.autoCloseOnMouseLeave &&
      this.props.isOpen &&
      !nextProps.isOpen
    ) {
      this.setState({ holdOpen: true })
      this.timeoutId = setTimeout(() => {
        if (!this.state.isMouseOver) {
          this.setState({ holdOpen: false })
        }
      }, TIME_IT_TAKES_TO_MOVE_MOUSE_FROM_ANCHOR_TO_TOOLTIP)
    }
  }

  supportsTouch() {
    return 'ontouchstart' in document.documentElement
  }

  addMouseDownListener() {
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener(
        this.supportsTouch() ? 'touchstart' : 'mousedown',
        this.onMouseDown
      )
    }
  }

  removeMouseDownListener() {
    if (typeof document !== 'undefined' && document.removeEventListener) {
      document.removeEventListener(
        this.supportsTouch() ? 'touchstart' : 'mousedown',
        this.onMouseDown
      )
    }
  }

  componentDidMount() {
    this.addMouseDownListener()
  }

  componentWillUnmount() {
    this.removeMouseDownListener()
  }

  isDescendant(parent, child) {
    let node = child.parentNode
    while (node !== null) {
      if (node === parent) {
        return true
      }
      node = node.parentNode
    }
    return false
  }

  onMouseDown = e => {
    // If user clicks outside of tooltip but not on anchor then close when...
    // - "hover" mode is enabled (!!autoCloseOnMouseLeave === true)
    // - OR "click outside to close" mode is enabled (!!alwaysCloseOnClickOutside === true)
    if (
      (this.props.autoCloseOnMouseLeave ||
        this.props.alwaysCloseOnClickOutside) &&
      !this.isDescendant(this.tooltip.parentNode, e.target) &&
      this.props.onClose
    ) {
      this.props.onClose()
      this.setState({ holdOpen: false })
    }
  }

  onMouseEnter = () => {
    this.setState({ isMouseOver: true })
  }

  onMouseLeave = () => {
    this.setState({ holdOpen: false, isMouseOver: false })
  }

  captureRef = me => {
    this.tooltip = me
  }

  render() {
    const {
      autoCloseOnMouseLeave,
      children,
      className,
      elmId,
      onClose,
      position,
    } = this.props
    const openCls =
      this.props.isOpen || this.state.holdOpen ? cls['is-open'] : null
    const errorCls = this.props.error ? cls.error : null

    return (
      <div
        className={cx(cls.tooltip, openCls, cls[position], errorCls, className)}
        data-elm-id={elmId}
        ref={this.captureRef}
        onMouseLeave={this.onMouseLeave}
        onMouseEnter={this.onMouseEnter}
      >
        {onClose && !autoCloseOnMouseLeave ? (
          <Icon
            name='close'
            size='small'
            data-elm-id={`${elmId}_close`}
            className={cx(cls['close-icon'])}
            onClick={onClose}
          />
        ) : null}
        <div className={cx(cls.content)} data-elm-id={`${elmId}_content`}>
          {children}
        </div>
        <span className={cx(cls['arrow-border'])} />
        <span className={cx(cls.arrow)} />
      </div>
    )
  }
}

export default Tooltip
export { TooltipAnchor }
